import React, { useEffect } from "react";
import { Card } from "react-bootstrap";
import { FiArrowLeft } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { submitContactForm, resetContactState } from '../../../redux/slices/contactSlice';
import { showSuccess, showError } from '../../../components/toasts';

import { ReactComponent as EmailContactIcon } from "../../../assets/images/email-contact.svg";
import NoIndexSEO from "../../../components/SEO/NoIndexSEO";
import { seoConfig } from "../../../utils/seoConfig";
import GuidedTour from "../../../components/tour/GuidedTour";
import useTour from "../../../components/tour/useTour";
import getContactTourSteps from "./contactTourSteps";
import { activityEnd, activityStart } from "../../../redux/slices/trackActivitySlice";
import usePageTracking from "../../../hooks/usePageTracking";

// Validation schema
const contactSchema = yup.object().shape({
  // name: yup
  //   .string()
  //   .required('Name is required')
  //   .min(2, 'Name must be at least 2 characters')
  //   .max(50, 'Name must not exceed 50 characters'),
  // email: yup
  //   .string()
  //   .required('Email is required')
  //   .email('Please enter a valid email address'),
  message: yup
    .string()
    .required('Message is required')
    .min(10, 'Message must be at least 10 characters')
    .max(500, 'Message must not exceed 500 characters'),
});

const ContactUs = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authStore = useSelector((state) => state.auth);
  // Get state from Redux store
  const {
    isLoading,
    isSuccess,
    isError,
    successMessage,
    errorMessage,
  } = useSelector((state) => state.contact);

  // React Hook Form setup
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      message: '',
    },
    resolver: yupResolver(contactSchema),
  });

  const onSubmit = (values) => {
    if (values) {
      const contactData = {
        name: authStore?.user?.full_name || '',
        email: authStore?.user?.email || '',
        message: values?.message || '',
      };
      dispatch(submitContactForm(contactData));
    }
  };

  // Track Activity 
 usePageTracking("CONTACT_US");

 useEffect(() => {
   const payload = {
     page_name: "CONTACT_US",
     platform: "web",
   };

   dispatch(activityStart(payload));
 }, [dispatch]);

 useEffect(() => {
   return () => {
     dispatch(
       activityEnd({
         page_name: "CONTACT_US",
       }),
     );
   };
 }, [dispatch]);

  

  // Handle success/error
  useEffect(() => {
    if (isSuccess && successMessage) {
      showSuccess(successMessage);
      reset(); // Reset form on success
      dispatch(resetContactState());
    }

    if (isError && errorMessage) {
      showError(errorMessage);
      dispatch(resetContactState());
    }
  }, [isSuccess, isError, successMessage, errorMessage, dispatch, reset]);

  // Guided tour — auto-starts on first visit, replayable from the header's
  // tour button (pageKey "contact").
  const { isOpen: isTourOpen, finish: finishTour } = useTour("contact");

  return (
    <>
    <NoIndexSEO title={seoConfig.contactUs.title} />
    <GuidedTour
      isOpen={isTourOpen}
      steps={getContactTourSteps()}
      onDone={finishTour}
    />
    <div className="contact-content">
      <div className="app-page-title">
        <div className="page-title-heading d-flex align-items-center">
          <FiArrowLeft
            size={40}
            className="icon cursor-pointer"
            onClick={() => navigate("/profile")}
          />
          <h3 className="page-title">Contact us</h3>
        </div>
      </div>

      {/* Email card */}
      <Card className="email-content" data-tour="co-email">
        <Card.Body>
          <div className="icon">
            <EmailContactIcon />
          </div>
          <div className="text">
            <p className="label">Mail us at</p>
            <p className="email">support@mapmypay.com</p>
          </div>
        </Card.Body>
      </Card>

      {/* Form */}
      <Card data-tour="co-form">
        <Card.Body>
          <div className="innertitle-subtitle">
            <div className="inner-title pb-0">Get in touch</div>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
            <div className="row">
              <div className="col-md-6">
                <div className="form-group" data-tour="co-name">
                  <label className="col-label" htmlFor="name">
                    Name
                  </label>
                  <Controller
                    id="name"
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="text"
                        className={`col-input ${errors.name ? 'is-invalid' : ''}`}
                        placeholder={authStore?.user?.full_name}
                        disabled
                        autoComplete="off"
                      />
                    )}
                  />
                  {errors.name && (
                    <div className="invalid-feedback d-block">
                      {errors.name.message}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label className="col-label" htmlFor="email">
                    Email address
                  </label>
                  <Controller
                    id="email"
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="email"
                        className={`col-input ${errors.email ? 'is-invalid' : ''}`}
                        placeholder={authStore?.user?.email}
                        disabled
                        autoComplete="off"
                      />
                    )}
                  />
                  {errors.email && (
                    <div className="invalid-feedback d-block">
                      {errors.email.message}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-12">
                <div className="form-group" data-tour="co-message">
                  <label className="col-label" htmlFor="message">
                    Message
                  </label>
                  <Controller
                    id="message"
                    name="message"
                    control={control}
                    render={({ field }) => (
                      <textarea
                        {...field}
                        rows="5"
                        className={`col-textarea ${errors.message ? 'is-invalid' : ''}`}
                        placeholder="Type here"
                        autoComplete="off"
                      />
                    )}
                  />
                  {errors.message && (
                    <div className="invalid-feedback d-block">
                      {errors.message.message}
                    </div>
                  )}
                </div>
              </div>
              {/* Submit button */}
              <div className="form-group text-center mb-0">
                <button
                  type="submit"
                  className="btn btn-primary"
                  data-tour="co-send"
                  disabled={isLoading}
                >
                  {isLoading ? "Sending..." : "Send"}
                </button>
              </div>
            </div>
          </form>
        </Card.Body>
      </Card>
    </div>
    </>
  );
};

export default ContactUs;